import React, { createContext, useContext, useState } from 'react';

const CostsContext = createContext();

export const useCosts = () => {
  const context = useContext(CostsContext);
  if (!context) {
    throw new Error('useCosts must be used within CostsProvider');
  }
  return context;
};

const initialState = {
  property: {
    purchasePrice: 250000,
    propertyType: 'eigentumswohnung', // einfamilienhaus, mehrfamilienhaus
    squareMeters: 75,
    numberOfUnits: 1,
    yearBuilt: 1985,
    bundesland: 'berlin',
    city: 'berlin',
    landSharePercent: 40,
    energieausweis: 'D',      // A+ through H (for CO2 cost allocation)
    heatingType: 'gas',       // gas, oil, district, electric, pellet
  },

  taxEntity: {
    type: 'private', // gmbh, immo_gmbh, gmbh_co_kg, stiftung
    personalTaxRate: 42,
    municipality: 'berlin', // for Gewerbesteuer Hebesatz
    kommanditeinlage: 25000, // for KG §15a tracking
    kirchensteuerEnabled: false,
    kirchensteuerRate: 0.09, // 8% Bayern/BW, 9% elsewhere
    otherAnnualIncome: 0, // Other income for marginal ESt bracket
  },

  projection: {
    horizonYears: 15,
    inflationRate: 2,
    rentIncreaseType: 'regular', // staffelmiete, indexmiete
    cpiGrowthRate: 2,
    staffelmieteAmount: 0,
    appreciationRate: 2,
    landAppreciationRate: 3,
    buildingAppreciationRate: 1,
  },

  revenue: {
    rentalType: 'longterm', // shortterm
    kaltmietePerSqm: 13,
    vacancyRatePercent: 3,
    mietspiegelRent: 13,
    mietpreisbremseActive: true,
    kappungsgrenzeRate: 0.15,
    // Short-term fields
    nightlyRate: 95,
    availableNights: 250,
    occupancyRate: 72,
    platformFeePercent: 15,
  },

  werbungskosten: {
    disagioPercent: 0,                    // 0-5% of loan amount, Year 1 only
    steuerberaterAnnual: 500,             // €/year
    sonstigePauschale: 200,              // €/year miscellaneous
    mietausfallversicherungPercent: 0,    // 0-5% of annual rent
  },

  vat: {
    vatLiable: 'auto',        // auto | yes | no
    hotelServices: false,      // triggers GewSt risk
    eigennutzung: false,       // triggers Zweitwohnungssteuer
  },

  purchase: {
    equityCapital: 80000,
    debtCapital: 202000,
  },

  exit: {
    brokerCommissionPercent: 3.57,
  },

  startup: {
    grunderwerbsteuer: 0, // auto-calculated
    notarkosten: 0,
    grundbuchkosten: 0,
    maklerkosten: 0,
    renovationBudget: 0,
    renovationMaintenanceSharePercent: 20,
  },

  fixedRecurring: {
    grundsteuer: 300,
    gebaeudeversicherung: 400,
    hausgeld: 120, // monthly, only for Eigentumswohnung
    hausgeldNichtUmlagefaehigPercent: 30,
    propertyManagementPercent: 0, // 0 = self-managed
    custom: [],
  },

  variableRecurring: {
    maintenancePercent: { lower: 1.0, upper: 1.5 },
    vacancyProvision: 0,
    tenantTurnoverCost: 1500,
    custom: [],
  },

  scheduledCapex: {},

  emergencyFund: {
    annualReservePercent: 2,
  },

  depreciation: {
    afaMethod: 'standard', // degressive, denkmalschutz
    renovationTreatment: 'capitalize', // expense
    renovationDepreciationYears: 10,
  },

  loan: {
    interestRate: 3.65,
    zinsbindungYears: 10,
    tilgungPercent: 2.0,
    tilgungsfreieJahre: 0,
    refinancingRateSpread: 0,
  },
};

export const CostsProvider = ({ children }) => {
  const [costs, setCosts] = useState(initialState);

  const updateProperty = (updates) => {
    setCosts(prev => {
      const newState = {
        ...prev,
        property: { ...prev.property, ...updates }
      };
      // Auto-set Kirchensteuer rate based on Bundesland
      if (updates.bundesland) {
        const isBayernBW = updates.bundesland === 'baden_wuerttemberg' || updates.bundesland === 'bayern';
        newState.taxEntity = {
          ...prev.taxEntity,
          kirchensteuerRate: isBayernBW ? 0.08 : 0.09,
        };
      }
      return newState;
    });
  };

  const updateTaxEntity = (updates) => {
    setCosts(prev => ({
      ...prev,
      taxEntity: { ...prev.taxEntity, ...updates }
    }));
  };

  const updateProjection = (updates) => {
    setCosts(prev => ({
      ...prev,
      projection: { ...prev.projection, ...updates }
    }));
  };

  const updateRevenue = (updates) => {
    setCosts(prev => ({
      ...prev,
      revenue: { ...prev.revenue, ...updates }
    }));
  };

  const updatePurchase = (updates) => {
    setCosts(prev => ({
      ...prev,
      purchase: { ...prev.purchase, ...updates }
    }));
  };

  const updateStartup = (updates) => {
    setCosts(prev => ({
      ...prev,
      startup: { ...prev.startup, ...updates }
    }));
  };

  const updateFixedRecurring = (updates) => {
    setCosts(prev => ({
      ...prev,
      fixedRecurring: { ...prev.fixedRecurring, ...updates }
    }));
  };

  const updateVariableRecurring = (updates) => {
    setCosts(prev => ({
      ...prev,
      variableRecurring: { ...prev.variableRecurring, ...updates }
    }));
  };

  const updateScheduledCapex = (year, items) => {
    setCosts(prev => ({
      ...prev,
      scheduledCapex: { ...prev.scheduledCapex, [year]: items }
    }));
  };

  const updateEmergencyFund = (updates) => {
    setCosts(prev => ({
      ...prev,
      emergencyFund: { ...prev.emergencyFund, ...updates }
    }));
  };

  const updateDepreciation = (updates) => {
    setCosts(prev => ({
      ...prev,
      depreciation: { ...prev.depreciation, ...updates }
    }));
  };

  const updateLoan = (updates) => {
    setCosts(prev => ({
      ...prev,
      loan: { ...prev.loan, ...updates }
    }));
  };

  const updateWerbungskosten = (updates) => {
    setCosts(prev => ({
      ...prev,
      werbungskosten: { ...prev.werbungskosten, ...updates }
    }));
  };

  const updateVat = (updates) => {
    setCosts(prev => ({
      ...prev,
      vat: { ...prev.vat, ...updates }
    }));
  };

  const value = {
    costs,
    updateProperty,
    updateTaxEntity,
    updateProjection,
    updateRevenue,
    updatePurchase,
    updateStartup,
    updateFixedRecurring,
    updateVariableRecurring,
    updateScheduledCapex,
    updateEmergencyFund,
    updateDepreciation,
    updateLoan,
    updateWerbungskosten,
    updateVat,
  };

  return (
    <CostsContext.Provider value={value}>
      {children}
    </CostsContext.Provider>
  );
};
